import { TDocumentVerificationStatus } from './types';

/**
 * How a DVS status should read to a human.
 *
 * Only `NotApplicable` is spaced. The compound values that predate it (`SystemError`, `DataError`)
 * are deliberately left exactly as administrators already read them, so this change alters no
 * existing label; the map is exhaustive so a status cannot be added without a labelling decision.
 */
export const DVS_STATUS_LABELS: Record<keyof typeof TDocumentVerificationStatus, string> = {
  Pending: 'Pending',
  Passed: 'Passed',
  Failed: 'Failed',
  SystemError: 'SystemError',
  DataError: 'DataError',
  NotApplicable: 'Not Applicable',
};

/**
 * The visual weight a DVS status carries.
 *
 * `neutral` is not a softer `negative`: `NotApplicable` records that no source check was, or ever
 * will be, attempted for the document. Colouring it as an error would tell an administrator that
 * something went wrong when nothing did.
 */
export type TDvsStatusTone = 'positive' | 'negative' | 'neutral';

export function toDvsStatusLabel(status: keyof typeof TDocumentVerificationStatus): string {
  return DVS_STATUS_LABELS[status] ?? status;
}

export function toDvsStatusTone(status: keyof typeof TDocumentVerificationStatus): TDvsStatusTone {
  switch (status) {
    case 'Passed':
      return 'positive';
    case 'NotApplicable':
      return 'neutral';
    default:
      return 'negative';
  }
}
